import React from 'react';
import { useNode, useEditor } from '@craftjs/core';
import { Button } from '@/components/ui/button';
import { normalizeBuilderLink, isBlankHtml, navigateBuilderCta } from '@/utils/functions';
import { htmlSeguro } from '../../utils/htmlSeguro';
import { AreaDePiezas, Pieza } from '../../utils/piezaLibre';

// Alineación del bloque y de la fila de CTAs. Mapas literales y no
// `text-${x}`: Tailwind purga por coincidencia de texto y una clase armada por
// interpolación puede desaparecer del bundle sin aviso.
// Espejo de goautos-admin/src/components/builder2/sections/initialfold/HeroWithVideoEmbed.tsx
const ALINEACION = { left: 'text-left', center: 'text-center', right: 'text-right' } as const;
const JUSTIFICADO = { left: 'justify-start', center: 'justify-center', right: 'justify-end' } as const;

interface HeroWithVideoEmbedProps {
  title?: string;
  subtitle?: string;
  buttonText?: string;
  buttonLink?: string;
  buttonTextSecondary?: string;
  buttonLinkSecondary?: string;
  buttonBgColor?: string;
  buttonTextColor?: string;
  buttonSecondaryBgColor?: string;
  buttonSecondaryTextColor?: string;
  videoUrl?: string;
  overlayColor?: string;
  overlayOpacity?: number;
  textColor?: string;
  textAlignment?: 'left' | 'center' | 'right';
  height?: string;
  maxWidth?: string;
  borderRadius?: string;
  /**
   * En MÓVIL el hero va a sangre y se mete debajo de la barra de navegación.
   *
   * Apagado por defecto y NO va en `craft.props`: los nodos ya publicados no lo
   * traen, sale `undefined` y el hero se dibuja exactamente como siempre. Es el
   * mismo patrón que `cardSpec1..5` — cero migración.
   *
   * Desde `md` no hace nada: escritorio queda igual.
   */
  mobileFullBleed?: boolean;
  children?: React.ReactNode;
}

/** Desde acá el hero vuelve a su caja de siempre. Igual que el `sm:px-6`. */
const CORTE_MOVIL = 768;

/**
 * Marca la barra de navegación mientras este hero está a sangre debajo de ella.
 *
 * La regla que la pinta transparente vive en un `<style>` con `@media`, no en
 * un estilo en línea: el navbar se pinta el fondo a sí mismo con `style=`, así
 * que hace falta `!important` para ganarle, y con la media query escritorio no
 * se entera aunque el atributo quedara puesto.
 */
const ATRIBUTO_BARRA_SOBRE_HERO = 'data-barra-sobre-hero';

const CSS_BARRA_SOBRE_HERO = `@media (max-width: ${CORTE_MOVIL - 1}px){`
  + `[${ATRIBUTO_BARRA_SOBRE_HERO}]{background-color:transparent!important;`
  + `box-shadow:none!important;backdrop-filter:none!important}}`;

/** Lo que hay que compensar para que el hero llegue a los bordes de la pantalla. */
type AjusteASangre = {
  /** Cuánto subir para quedar debajo de la barra. 0 = no subir. */
  solape: number;
  /** Padding del contenedor de sección que encajona al hero. */
  padIzq: number;
  padDer: number;
  padArriba: number;
  /** La barra, si la hay, para poder verla transparente sobre el video. */
  barra: HTMLElement | null;
};

const SIN_AJUSTE: AjusteASangre = { solape: 0, padIzq: 0, padDer: 0, padArriba: 0, barra: null };

/**
 * Qué hay que compensar para que el hero ocupe la pantalla de verdad.
 *
 * Son dos cosas distintas y cada una con su propia condición:
 *
 * - **La barra de arriba.** Sólo se sube si EXISTE una barra pegada arriba de
 *   todo y este hero es lo primero que viene después de ella. En medio de una
 *   página, subirlo lo montaría encima de la sección anterior. El alto se mide,
 *   no se supone.
 * - **El padding de la sección.** El contenedor que el Builder dibuja alrededor
 *   trae su propio padding —en Ansar, `48px 16px`— y eso deja al hero
 *   encajonado aunque él no ponga márgenes. Se compensa con márgenes negativos
 *   en vez de tocar el padding guardado: así el valor del Builder queda intacto
 *   y escritorio no se entera, porque esto sólo corre abajo de `md`.
 *
 * Todo devuelve 0 fuera de móvil.
 */
const medirAjusteASangre = (hero: HTMLElement | null): AjusteASangre => {
  if (!hero || typeof window === 'undefined') return SIN_AJUSTE;
  const doc = hero.ownerDocument;
  const vista = doc.defaultView || window;
  if (doc.documentElement.clientWidth >= CORTE_MOVIL) return SIN_AJUSTE;

  // ── La barra: pegada arriba, del ancho de la pantalla y de alto de barra.
  // Se busca por forma y no por una clase, para no atarse al navbar.
  const barra = Array.from(doc.querySelectorAll<HTMLElement>('header, div'))
    .find((el) => {
      const cs = vista.getComputedStyle(el);
      if (cs.position !== 'sticky' && cs.position !== 'fixed') return false;
      const r = el.getBoundingClientRect();
      return r.top + vista.scrollY <= 1
        && r.width >= doc.documentElement.clientWidth - 2
        && r.height >= 40 && r.height <= 140;
    }) || null;

  const altoBarra = barra ? Math.round(barra.getBoundingClientRect().height) : 0;
  // ¿El hero arranca justo donde termina la barra? Se compara contra el
  // documento y no contra la ventana, así no depende del scroll.
  const topEnDocumento = hero.getBoundingClientRect().top + vista.scrollY;
  const esLoPrimero = topEnDocumento <= altoBarra + 4;
  const solape = barra && esLoPrimero ? altoBarra : 0;

  // ── El padding de la sección. Se busca el primer ancestro que encajone: el
  // que tiene padding y es más ancho que el hero. Se corta a los pocos niveles
  // porque la sección está ahí nomás; más arriba ya es el armazón de la página.
  let padIzq = 0, padDer = 0, padArriba = 0;
  let p = hero.parentElement;
  for (let i = 0; i < 4 && p; i++) {
    const cs = vista.getComputedStyle(p);
    const pi = Math.round(parseFloat(cs.paddingLeft) || 0);
    const pd = Math.round(parseFloat(cs.paddingRight) || 0);
    if (pi > 0 || pd > 0) {
      padIzq = pi;
      padDer = pd;
      // El padding de arriba sólo se saca si el hero es lo primero DENTRO de
      // ese contenedor. Si tiene algo encima, quitarlo lo solaparía.
      const primero = p.firstElementChild;
      const heroEsElPrimero = primero === hero
        || (primero != null && primero.contains(hero));
      if (heroEsElPrimero) padArriba = Math.round(parseFloat(cs.paddingTop) || 0);
      break;
    }
    p = p.parentElement;
  }

  return { solape, padIzq, padDer, padArriba, barra };
};

/**
 * Cuánto hay que desplazar el bloque de piezas para que quede anclado al borde
 * inferior del hero. Geometría pura: entra y sale en números, así se puede
 * probar sin montar nada.
 *
 * Devuelve 0 —no mover— si alguna pieza vive en la mitad de ARRIBA. El
 * desplazamiento mueve el área entera, o sea todas las piezas a la vez: eso es
 * lo que se quiere para el bloque de texto (título, subtítulo y botones bajan
 * juntos y conservan su composición), pero se llevaría al fondo una pieza que
 * alguien puso arriba a propósito.
 *
 * El margen inferior es proporcional, con un piso de 120px que reserva la
 * esquina del botón flotante de WhatsApp: sin él, en una pantalla de 568 el
 * subtítulo se le metía debajo.
 */
export const ajusteDeAnclaje = (
  hero: { top: number; height: number },
  piezas: { top: number; bottom: number }[],
): number => {
  if (!piezas.length) return 0;
  const mitad = hero.top + hero.height / 2;
  if (Math.min(...piezas.map((p) => p.top)) < mitad) return 0;
  const margen = Math.max(hero.height * 0.12, 120);
  return Math.round((hero.top + hero.height) - margen - Math.max(...piezas.map((p) => p.bottom)));
};

const getBackgroundEmbedUrl = (url: string): string | null => {
  if (!url) return null;

  let m = url.match(/streamable\.com\/(?:e\/)?([a-zA-Z0-9]+)/);
  if (m) {
    return `https://streamable.com/e/${m[1]}?autoplay=1&muted=1&loop=1&nocontrols=1`;
  }

  m = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/
  );
  if (m) {
    const id = m[1];
    return `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&loop=1&playlist=${id}&controls=0&modestbranding=1&showinfo=0&rel=0&iv_load_policy=3&playsinline=1`;
  }

  m = url.match(/vimeo\.com\/(\d+)/);
  if (m) {
    return `https://player.vimeo.com/video/${m[1]}?autoplay=1&loop=1&muted=1&background=1`;
  }

  return null;
};

export const HeroWithVideoEmbed = ({
  title = 'Encuentra tu próximo auto',
  subtitle = 'Amplio inventario de autos seminuevos verificados y con garantía',
  buttonText = 'Ver vehículos',
  buttonLink = '#vehicles',
  buttonTextSecondary = 'Contactar',
  buttonLinkSecondary = '#contact',
  buttonBgColor = '#3b82f6',
  buttonTextColor = '#ffffff',
  buttonSecondaryBgColor = 'transparent',
  buttonSecondaryTextColor = '#ffffff',
  videoUrl = '',
  overlayColor = '#000000',
  overlayOpacity = 0.5,
  textColor = '#ffffff',
  textAlignment = 'center',
  height = '500px',
  maxWidth = '1280px',
  borderRadius = '16px',
  mobileFullBleed = false,
  children,
}: HeroWithVideoEmbedProps) => {
  const { connectors, selected } = useNode((state) => ({
    selected: state.events.selected,
  }));

  // Envoltorio externo: es lo que se sube para meter el hero debajo de la barra.
  const envoltorioRef = React.useRef<HTMLDivElement | null>(null);
  // Los dos efectos van por separado a propósito. Ir a sangre es seguro en
  // cualquier posición de la página; SUBIRSE debajo de la barra sólo lo es si el
  // hero es lo primero que hay. Mezclarlos dejaría el hero encajonado en una
  // página que no empieza con él, o montado encima de la sección de arriba.
  const [esMovil, setEsMovil] = React.useState(false);
  const [ajuste, setAjuste] = React.useState<AjusteASangre>(SIN_AJUSTE);

  // Antes de pintar, no después: con `useEffect` el hero se dibujaba una vez
  // encajonado y saltaba a sangre en el cuadro siguiente. En el servidor no hay
  // layout que medir, así que ahí cae al efecto normal y React no protesta.
  const useEfectoDeLayout = typeof window !== 'undefined'
    ? React.useLayoutEffect
    : React.useEffect;

  useEfectoDeLayout(() => {
    if (!mobileFullBleed) { setEsMovil(false); setAjuste(SIN_AJUSTE); return; }
    const el = envoltorioRef.current;
    if (!el) return;
    const doc = el.ownerDocument;
    const vista = doc.defaultView || window;
    const medir = () => {
      // El primer render tiene que coincidir con el del servidor —si no, React
      // avisa de hidratación—, así que esto se decide recién al montar.
      setEsMovil(doc.documentElement.clientWidth < CORTE_MOVIL);
      setAjuste(medirAjusteASangre(envoltorioRef.current));
    };
    medir();
    // Un `ResizeObserver` sobre el propio hero y no un `resize` de la ventana:
    // en el canvas del Builder el ancho lo cambia el selector de dispositivo
    // redimensionando el iframe, y ese cambio no llega como `resize` a la
    // ventana que se estuviera escuchando.
    //
    // El constructor sale de la ventana DEL ELEMENTO, no de la de este módulo.
    // El canvas es un iframe y el código corre en el documento de arriba: un
    // observer creado en otro realm no avisa de los cambios de un elemento que
    // vive adentro. Medido con el hero montado en un iframe: pasar de 390 a
    // 1440 no disparaba nada.
    const RO = (vista as unknown as { ResizeObserver?: typeof ResizeObserver })
      .ResizeObserver || ResizeObserver;
    const obs = new RO(medir);
    obs.observe(el);
    obs.observe(doc.documentElement);
    vista.addEventListener('orientationchange', medir);
    return () => {
      obs.disconnect();
      vista.removeEventListener('orientationchange', medir);
    };
  }, [mobileFullBleed]);

  // La barra queda transparente sobre el hero, pero SÓLO mientras este hero
  // esté a sangre. Se marca con un atributo y el estilo va en una regla con
  // `@media`, así escritorio no se entera aunque el atributo quede puesto, y
  // los demás sitios —donde la opción está apagada— nunca lo reciben. No se
  // toca `transparentOnTop`, que es global y afectaría a todas las páginas.
  const barra = ajuste.barra;
  const barraTransparente = mobileFullBleed && esMovil && !!barra && ajuste.solape > 0;
  useEfectoDeLayout(() => {
    if (!barra) return;
    if (barraTransparente) barra.setAttribute(ATRIBUTO_BARRA_SOBRE_HERO, '');
    else barra.removeAttribute(ATRIBUTO_BARRA_SOBRE_HERO);
    return () => barra.removeAttribute(ATRIBUTO_BARRA_SOBRE_HERO);
  }, [barra, barraTransparente]);

  const aSangre = mobileFullBleed && esMovil;

  // ── El texto, anclado al borde de abajo ──────────────────────────────────
  //
  // Las piezas de la capa libre guardan su posición vertical en PÍXELES
  // (`--libre-my`), no en porcentaje como la horizontal. Con el hero midiendo
  // lo que mida la pantalla, un título clavado en 586px queda bien en un
  // teléfono y mal en el resto: medido contra la web que se replica, sobraban
  // 64px en uno de 932 y en uno de 568 el texto ni entraba sin scroll.
  //
  // Se corrige acá y no en `posicionLibre` porque eso cambiaría el
  // comportamiento de TODAS las piezas libres de todos los clientes. El bloque
  // se mide y se desplaza para que termine a una distancia proporcional del
  // borde inferior, con un piso que deja libre la esquina del botón flotante
  // —sin él, a 568 el subtítulo se le metía debajo—.
  const cajaRef = React.useRef<HTMLDivElement | null>(null);
  const [ajusteTexto, setAjusteTexto] = React.useState(0);
  const ajusteRef = React.useRef(0);
  ajusteRef.current = ajusteTexto;

  useEfectoDeLayout(() => {
    if (!aSangre) { setAjusteTexto(0); return; }
    const ajustar = () => {
      const caja = cajaRef.current;
      const area = caja?.querySelector<HTMLElement>('[data-area-piezas]');
      const piezas = area ? Array.from(area.querySelectorAll('[data-libre]')) : [];
      if (!caja || !piezas.length) { setAjusteTexto(0); return; }

      const r = caja.getBoundingClientRect();
      // Se resta el ajuste vigente para leer dónde CAERÍAN las piezas sin él;
      // si no, cada medición partiría del resultado de la anterior.
      const cajas = piezas.map((p) => {
        const b = p.getBoundingClientRect();
        return { top: b.top - ajusteRef.current, bottom: b.bottom - ajusteRef.current };
      });
      setAjusteTexto(ajusteDeAnclaje({ top: r.top, height: r.height }, cajas));
    };
    ajustar();
    const caja = cajaRef.current;
    // Mismo motivo que arriba: el observer tiene que ser el del realm del
    // elemento, que dentro del canvas es el del iframe.
    const vistaCaja = caja?.ownerDocument?.defaultView as
      (Window & { ResizeObserver?: typeof ResizeObserver }) | null | undefined;
    const RO = vistaCaja?.ResizeObserver || ResizeObserver;
    const obs = new RO(ajustar);
    if (caja) obs.observe(caja);
    return () => obs.disconnect();
  }, [aSangre, title, subtitle]);

  const { isEnabled } = useEditor((state) => ({
    isEnabled: state.options.enabled,
  }));

  const embedUrl = getBackgroundEmbedUrl(videoUrl || '');

  const overlayStyle: React.CSSProperties = {
    backgroundColor: overlayColor,
    opacity: overlayOpacity,
  };

  // Respeta el link del builder: navega si es ruta/URL, scroll si es #ancla.
  const scrollToVehicles = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isEnabled) return;
    navigateBuilderCta(buttonLink);
  };

  return (
    <div
      ref={envoltorioRef}
      className={`w-full flex justify-center ${aSangre ? '' : 'px-4 sm:px-6'}`}
      data-hero-a-sangre={aSangre ? '' : undefined}
      // Márgenes negativos y no `padding: 0` en la sección: el valor que el
      // dealer guardó en el Builder queda intacto, y como esto sólo se calcula
      // abajo de `md`, escritorio no cambia. El de arriba suma el alto de la
      // barra cuando corresponde subirse.
      style={aSangre ? {
        marginTop: -(ajuste.solape + ajuste.padArriba) || undefined,
        marginLeft: -ajuste.padIzq || undefined,
        marginRight: -ajuste.padDer || undefined,
        width: ajuste.padIzq || ajuste.padDer
          ? `calc(100% + ${ajuste.padIzq + ajuste.padDer}px)`
          : undefined,
      } : undefined}
    >
      <div
        ref={(el: HTMLDivElement | null) => {
          cajaRef.current = el;
          if (el) connectors.connect(el);
        }}
        style={{
          width: '100%',
          // A sangre el `maxWidth` del diseño sobra: es lo que dejaría el hero
          // centrado con aire a los costados en una pantalla ancha.
          maxWidth: aSangre ? 'none' : maxWidth,
          // El alto configurado es fijo y ningún teléfono mide eso: la web que
          // se replica ocupa siempre el 100% de la pantalla, así que a sangre
          // el hero hace lo mismo. Lo que antes impedía usar `100svh` a secas
          // —que el texto quedara fuera en un teléfono corto— lo resuelve ahora
          // el anclaje al borde inferior, que lo reubica según el alto real.
          height: aSangre ? '100svh' : height,
          position: 'relative',
          overflow: 'hidden',
          // Redondear algo que va de borde a borde no se ve: se recortarían las
          // esquinas contra el filo de la pantalla.
          borderRadius: aSangre ? 0 : borderRadius,
          // El fondo va a sangre, el CONTENIDO no: sin esto el título arranca
          // pegado al filo de la pantalla. Va acá y no en el área de piezas
          // porque el bloque de texto es `absolute` y su caja de referencia
          // incluye el padding del área — medido: ahí el padding no lo mueve, y
          // acá sí. El video queda igual: es `absolute; inset:0` y tapa la caja
          // entera, padding incluido.
          paddingLeft: aSangre ? 16 : undefined,
          paddingRight: aSangre ? 16 : undefined,
          color: textColor,
          backgroundColor: '#000',
          border: selected ? '1px dashed #1e88e5' : 'none',
        }}
        className='flex items-center'
      >
      {/* Video de fondo (cover) */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      >
        {embedUrl && (
          <iframe
            src={embedUrl}
            allow='autoplay; fullscreen; picture-in-picture'
            allowFullScreen
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: '177.78vh',
              height: '56.25vw',
              minWidth: '100%',
              minHeight: '100%',
              transform: 'translate(-50%, -50%)',
              border: 0,
            }}
            title='Hero video'
          />
        )}
      </div>

      {/* Overlay */}
      <div style={overlayStyle} className='absolute inset-0 z-[1]' />

      {/* Contenido */}
      <AreaDePiezas
        className='w-full relative z-[2]'
        // `transform` y no `top`: las piezas son `absolute` contra esta área,
        // así que moverla las lleva a todas juntas sin tocar sus coordenadas
        // —que son las que el dealer acomodó en el Builder— y sin cambiar el
        // layout de nada más.
        style={ajusteTexto ? { transform: `translateY(${ajusteTexto}px)` } : undefined}
      >
        <div
          className={`w-full ${ALINEACION[textAlignment]}`}
          style={{ margin: textAlignment === 'center' ? '0 auto' : '0' }}
        >
          <Pieza clave="titulo" tipo="texto" etiqueta="Título">
            {!isBlankHtml(title) && (
              <h1
                style={{ color: textColor }}
                // A sangre el título arranca una medida más abajo en los
                // teléfonos angostos: a 36px, "Tu próximo viaje comienza aquí."
                // mide 307px y en una pantalla de 320 no entra en los 288 que
                // quedan. Desde `sm` vuelve al tamaño de siempre, así que en
                // cualquier pantalla que no sea angosta no cambia nada, y con el
                // modo apagado tampoco.
                className={`${aSangre ? 'text-3xl sm:text-4xl' : 'text-4xl'} md:text-5xl font-bold mb-4`}
                dangerouslySetInnerHTML={htmlSeguro(title || '')}
              />
            )}
          </Pieza>
          <Pieza clave="subtitulo" tipo="texto" etiqueta="Descripción">
            {!isBlankHtml(subtitle) && (
              <p
                style={{ color: textColor }}
                // Mismo motivo que el título. Medido a 320px: el subtítulo se
                // iba a tres renglones, terminaba en y=783 y se metía 7px debajo
                // del botón flotante de WhatsApp, que arranca en 776. Con una
                // medida menos entra en dos y deja de tocarlo.
                className={`${aSangre ? 'text-base sm:text-lg' : 'text-lg'} md:text-xl mb-8`}
                dangerouslySetInnerHTML={htmlSeguro(subtitle || '')}
              />
            )}
          </Pieza>
          <Pieza clave="botones" etiqueta="Botones">
            {(!isBlankHtml(buttonText) || !isBlankHtml(buttonTextSecondary)) && (
              <div className={`flex flex-wrap gap-4 ${JUSTIFICADO[textAlignment]}`}>
                {!isBlankHtml(buttonText) && (
                  <Button
                    className='px-8 py-3 rounded-md transition-colors'
                    style={{
                      backgroundColor: buttonBgColor,
                      color: buttonTextColor,
                    }}
                    onClick={scrollToVehicles}
                  >
                    <span dangerouslySetInnerHTML={htmlSeguro(buttonText || '')} />
                  </Button>
                )}
                {!isBlankHtml(buttonTextSecondary) && (
                  <Button
                    asChild
                    variant='outline'
                    className='px-8 py-3 rounded-md border-2 transition-colors'
                    style={{
                      backgroundColor: buttonSecondaryBgColor,
                      color: buttonSecondaryTextColor,
                      borderColor: buttonSecondaryTextColor,
                    }}
                  >
                    <a
                      href={normalizeBuilderLink(buttonLinkSecondary)}
                      dangerouslySetInnerHTML={htmlSeguro(buttonTextSecondary || '')}
                    />
                  </Button>
                )}
              </div>
            )}
          </Pieza>
          {children}
        </div>
      </AreaDePiezas>
      </div>
      {/* La regla de la barra transparente. Va al final y no antes del hero:
          delante sería el primer hijo del envoltorio, y más de una cosa —los
          tests entre ellas— da por sentado que ese primer hijo es el hero. */}
      {barraTransparente && (
        <style dangerouslySetInnerHTML={{ __html: CSS_BARRA_SOBRE_HERO }} />
      )}
    </div>
  );
};

const HeroWithVideoEmbedSettingsStub = () => null;

HeroWithVideoEmbed.craft = {
  displayName: 'HeroWithVideoEmbed',
  props: {
    title: 'Encuentra tu próximo auto',
    subtitle:
      'Amplio inventario de autos seminuevos verificados y con garantía',
    buttonText: 'Ver vehículos',
    buttonLink: '#vehicles',
    buttonTextSecondary: 'Contactar',
    buttonLinkSecondary: '#contact',
    buttonBgColor: '#3b82f6',
    buttonTextColor: '#ffffff',
    buttonSecondaryBgColor: 'transparent',
    buttonSecondaryTextColor: '#ffffff',
    videoUrl: '',
    overlayColor: '#000000',
    overlayOpacity: 0.5,
    textColor: '#ffffff',
    textAlignment: 'center',
    height: '500px',
    maxWidth: '1280px',
    borderRadius: '16px',
  },
  related: {
    settings: HeroWithVideoEmbedSettingsStub,
  },
  rules: {
    canDrag: () => true,
    canDrop: () => true,
    canMoveIn: () => true,
  },
  isCanvas: true,
};
